import { Link, useLocation } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { FluentEmoji } from '@/components/ui/fluent-emoji'
import { ArrowLeft, Menu, X, ChevronRight, ChevronLeft } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'

const navGroups = [
  {
    label: 'Overview',
    items: [
      { href: '/admin', label: 'Overview', emoji: '📊' },
      { href: '/admin/pulse', label: 'Pulse', emoji: '💜' },
    ],
  },
  {
    label: 'Business',
    items: [
      { href: '/admin/subscriptions', label: 'Subscriptions', emoji: '💳' },
      { href: '/admin/economy', label: 'Economy', emoji: '💎' },
      { href: '/admin/bi', label: 'BI', emoji: '📡' },
    ],
  },
  {
    label: 'Users & Engagement',
    items: [
      { href: '/admin/users', label: 'Users', emoji: '👥' },
      { href: '/admin/analytics', label: 'Analytics', emoji: '📈' },
      { href: '/admin/engagement', label: 'Engagement', emoji: '🎮' },
    ],
  },
  {
    label: 'Content',
    items: [
      { href: '/admin/challenges', label: 'Challenges', emoji: '🎯' },
      { href: '/admin/badges', label: 'Badges', emoji: '🏆' },
    ],
  },
  {
    label: 'System',
    items: [
      { href: '/admin/notifications', label: 'Notifications', emoji: '🔔' },
      { href: '/admin/push', label: 'Push', emoji: '📢' },
      { href: '/admin/bugs', label: 'Bugs', emoji: '🐛' },
      { href: '/admin/lights', label: 'Lights', emoji: '💡' },
    ],
  },
]

// Desktop Sidebar
function DesktopSidebar() {
  const location = useLocation()
  const [expanded, setExpanded] = useState(false)
  const [pinned, setPinned] = useState(false)

  const isOpen = expanded || pinned

  return (
    <div
      className="hidden lg:flex fixed left-0 top-0 h-full z-50 flex-col"
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={() => !pinned && setExpanded(false)}
    >
      <motion.div
        animate={{ width: isOpen ? 220 : 56 }}
        transition={{ duration: 0.2, ease: 'easeInOut' }}
        className="h-full bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 flex flex-col shadow-lg overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center h-14 px-3 border-b border-slate-200 dark:border-slate-800 shrink-0">
          {isOpen ? (
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                <FluentEmoji emoji="🔐" size={20} />
                <span className="font-bold text-slate-900 dark:text-white text-sm uppercase tracking-wider whitespace-nowrap">
                  Admin
                </span>
              </div>
              <button
                onClick={() => setPinned(!pinned)}
                className="p-1 rounded-md text-slate-400 hover:text-slate-700 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                title={pinned ? 'Unpin sidebar' : 'Pin sidebar'}
              >
                {pinned ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-center w-full">
              <FluentEmoji emoji="🔐" size={20} />
            </div>
          )}
        </div>

        {/* Back to App */}
        <Link
          to="/"
          className="flex items-center gap-2.5 h-10 mx-2 mt-2 px-2 rounded-lg text-slate-500 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors shrink-0"
        >
          <ArrowLeft size={18} className="shrink-0" />
          {isOpen && (
            <span className="text-xs font-medium whitespace-nowrap">Back to App</span>
          )}
        </Link>

        {/* Nav Groups */}
        <nav className="flex-1 overflow-y-auto py-2 px-2 space-y-3">
          {navGroups.map((group) => (
            <div key={group.label}>
              {isOpen && (
                <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500 whitespace-nowrap">
                  {group.label}
                </div>
              )}
              <div className="space-y-0.5">
                {group.items.map((item) => {
                  const isActive = location.pathname === item.href
                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      className={`flex items-center gap-2.5 h-9 px-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
                        isActive
                          ? 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300'
                          : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white'
                      }`}
                      title={!isOpen ? item.label : undefined}
                    >
                      <span className="shrink-0 flex items-center justify-center w-5">
                        <FluentEmoji emoji={item.emoji} size={16} />
                      </span>
                      {isOpen && <span>{item.label}</span>}
                    </Link>
                  )
                })}
              </div>
            </div>
          ))}
        </nav>
      </motion.div>
    </div>
  )
}

// Mobile Header + Drawer
function MobileNav() {
  const location = useLocation()
  const [open, setOpen] = useState(false)

  // Close drawer on navigation
  useEffect(() => {
    setOpen(false)
  }, [location.pathname])

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [open])

  return (
    <div className="lg:hidden">
      {/* Mobile Top Bar */}
      <div className="fixed top-0 left-0 right-0 z-40 bg-white/90 dark:bg-slate-900/90 backdrop-blur-md border-b border-slate-200 dark:border-slate-800">
        <div
          className="relative"
          style={{ height: 'var(--native-safe-area-inset-top, env(safe-area-inset-top, 0px))' }}
        />
        <div className="flex items-center justify-between h-12 px-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setOpen(true)}
              className="p-2 -ml-2 rounded-lg text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <Menu size={22} />
            </button>
            <FluentEmoji emoji="🔐" size={18} />
            <span className="font-bold text-slate-900 dark:text-white text-sm uppercase tracking-wider">
              Admin
            </span>
          </div>
          <Link
            to="/"
            className="flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-900 dark:hover:text-white transition-colors"
          >
            <ArrowLeft size={14} />
            App
          </Link>
        </div>
      </div>

      {/* Mobile Drawer Overlay */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-50 bg-black/50"
              onClick={() => setOpen(false)}
            />
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              className="fixed left-0 top-0 bottom-0 z-50 w-72 bg-white dark:bg-slate-900 shadow-2xl flex flex-col"
            >
              {/* Drawer Header */}
              <div
                className="relative"
                style={{ height: 'var(--native-safe-area-inset-top, env(safe-area-inset-top, 0px))' }}
              />
              <div className="flex items-center justify-between h-14 px-4 border-b border-slate-200 dark:border-slate-800 shrink-0">
                <div className="flex items-center gap-2">
                  <FluentEmoji emoji="🔐" size={20} />
                  <span className="font-bold text-slate-900 dark:text-white text-sm uppercase tracking-wider">
                    Admin
                  </span>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  className="p-2 -mr-2 rounded-lg text-slate-500 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                  <X size={22} />
                </button>
              </div>

              {/* Back to App */}
              <Link
                to="/"
                className="flex items-center gap-2.5 h-11 mx-3 mt-3 px-3 rounded-lg text-slate-500 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                <ArrowLeft size={18} />
                <span className="text-sm font-medium">Back to App</span>
              </Link>

              {/* Nav Groups */}
              <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-4">
                {navGroups.map((group) => (
                  <div key={group.label}>
                    <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">
                      {group.label}
                    </div>
                    <div className="space-y-0.5">
                      {group.items.map((item) => {
                        const isActive = location.pathname === item.href
                        return (
                          <Link
                            key={item.href}
                            to={item.href}
                            className={`flex items-center gap-3 h-11 px-3 rounded-lg text-sm font-medium transition-all ${
                              isActive
                                ? 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300'
                                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white'
                            }`}
                          >
                            <FluentEmoji emoji={item.emoji} size={18} />
                            <span>{item.label}</span>
                          </Link>
                        )
                      })}
                    </div>
                  </div>
                ))}
              </nav>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}

export function AdminNav() {
  return (
    <>
      <DesktopSidebar />
      <MobileNav />
    </>
  )
}
